import { useState, useEffect } from 'react'
import Button from '../../../components/common/Button/Button'
import { getPolicySections } from '../../../services/leavePolicyService'

/**
 * EditPolicyModal Component
 * Modal form for editing leave policy sections
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close modal callback
 * @param {function} onSubmit - Submit form callback
 * @param {object} currentPolicy - Current policy data
 * @param {boolean} isLoading - Whether form is submitting
 */
const EditPolicyModal = ({ isOpen, onClose, onSubmit, currentPolicy, isLoading = false }) => {
  const sections = getPolicySections()
  const maxLength = 5000

  // Form state - one state for each policy section
  const [formData, setFormData] = useState({
    annualLeave: '',
    sickLeave: '',
    casualLeave: '',
    parentalLeave: '',
    publicHolidays: '',
    applicationProcess: ''
  })

  const [error, setError] = useState('')

  // Load current policy data when modal opens
  useEffect(() => {
    if (currentPolicy && isOpen) {
      setFormData({
        annualLeave: currentPolicy.annualLeave || '',
        sickLeave: currentPolicy.sickLeave || '',
        casualLeave: currentPolicy.casualLeave || '',
        parentalLeave: currentPolicy.parentalLeave || '',
        publicHolidays: currentPolicy.publicHolidays || '',
        applicationProcess: currentPolicy.applicationProcess || ''
      })
    }
  }, [currentPolicy, isOpen])

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    // Validate all fields
    for (const section of sections) {
      const value = formData[section.key]

      if (!value || !value.trim()) {
        setError(`${section.title} cannot be empty`)
        return
      }

      if (value.length > maxLength) {
        setError(`${section.title} exceeds maximum length of ${maxLength} characters`)
        return
      }
    }

    // Submit
    onSubmit(formData)
  }

  const handleClose = () => {
    if (!isLoading) {
      setError('')
      onClose()
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('psyber-modal-overlay')) {
      handleClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="psyber-modal-overlay" onClick={handleBackdropClick}>
      <div
        className="psyber-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '800px' }}
      >
        {/* Modal Header */}
        <div className="psyber-modal-header">
          <h3 style={{
            margin: 0,
            fontSize: 'var(--psyber-font-size-2xl)',
            color: 'var(--psyber-text)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <i className="fas fa-edit"></i>
            Edit Leave Policy
          </h3>
          <button
            onClick={handleClose}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: 'var(--psyber-text-secondary)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '2rem',
              height: '2rem',
              borderRadius: 'var(--psyber-radius-sm)',
              transition: 'var(--psyber-transition-fast)'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.currentTarget.style.background = 'var(--psyber-card-hover)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none'
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit}>
          {/* Error Display */}
          {error && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'var(--psyber-error-bg)',
              border: '2px solid var(--psyber-error)',
              borderRadius: 'var(--psyber-radius-md)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-exclamation-circle" style={{ fontSize: '1.5rem', color: 'var(--psyber-error)' }}></i>
              <span style={{ color: 'var(--psyber-error)', fontWeight: 600 }}>{error}</span>
            </div>
          )}

          {/* Help Text */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '0.75rem',
            background: 'var(--psyber-info-bg)',
            border: '1px solid var(--psyber-info)',
            borderRadius: 'var(--psyber-radius-md)',
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text-secondary)'
          }}>
            <i className="fas fa-info-circle" style={{ color: 'var(--psyber-info)', marginRight: '0.5rem' }}></i>
            <strong>Tip:</strong> Use bullet points (•) for better readability. Press Enter to create new lines.
          </div>

          {/* Policy Section Fields */}
          {sections.map((section, index) => {
            const value = formData[section.key] || ''
            const charCount = value.length
            const isNearLimit = charCount > maxLength * 0.9

            return (
              <div key={section.key} style={{ marginBottom: index < sections.length - 1 ? '1.5rem' : '1rem' }}>
                <label
                  htmlFor={section.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    fontSize: 'var(--psyber-font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--psyber-text)'
                  }}
                >
                  <i className={section.icon}></i>
                  {section.title} *
                </label>
                <textarea
                  id={section.key}
                  value={value}
                  onChange={(e) => handleChange(section.key, e.target.value)}
                  placeholder={`Enter ${section.title.toLowerCase()} policy details...`}
                  disabled={isLoading}
                  maxLength={maxLength}
                  className="psyber-input"
                  rows={6}
                  style={{
                    width: '100%',
                    resize: 'vertical',
                    minHeight: '120px',
                    fontFamily: 'inherit'
                  }}
                  required
                />
                <div style={{
                  marginTop: '0.25rem',
                  fontSize: 'var(--psyber-font-size-xs)',
                  color: isNearLimit ? 'var(--psyber-warning)' : 'var(--psyber-text-muted)',
                  textAlign: 'right'
                }}>
                  {charCount}/{maxLength} characters
                </div>
              </div>
            )
          })}

          {/* Modal Footer */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            paddingTop: '1.5rem',
            borderTop: '1px solid var(--psyber-card-border)',
            marginTop: '1rem'
          }}>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon="fas fa-save"
              loading={isLoading}
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Update Policy'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditPolicyModal
